import { z } from 'zod'

export const MarinaSchema = z.object({
  marina_id: z.number(),
  name: z.string(),
})

export type Marina = z.infer<typeof MarinaSchema>

export const BerthFormSchema = z
  .object({
    min_length: z
      .union([
        z.string(),
        z.number().min(1, 'Min length must be greater or equal to 1.'),
      ])
      .refine((value) => value !== '' && typeof value === 'number', {
        message: 'Min length is required.',
      }),
    max_length: z
      .union([
        z.string(),
        z.number().min(2, 'Max length must be greater or equal to 2.'),
      ])
      .refine((value) => value !== '' && typeof value === 'number', {
        message: 'Max length is required.',
      }),
    boat_types: z.array(z.string()),
    pricing: z.enum(['per_meter', 'flat_fee']),
    price_per_meter: z.number(),
    price_flat_fee: z.number(),
    berths: z
      .array(z.string())
      .min(1, 'At least one berth needs to be specified.'),
  })
  .refine((data) => data.max_length > data.min_length, {
    message: 'Max length must be greater or equal to min length.',
    path: ['max_length'],
  })

export const emptyBerthFormSchema: BerthFormType = {
  min_length: '',
  max_length: '',
  boat_types: [],
  pricing: 'per_meter',
  price_per_meter: 0,
  price_flat_fee: 0,
  berths: [],
}

export type BerthFormType = z.infer<typeof BerthFormSchema>

const AvailableDaySchema = z.object({
  date: z.string().refine((date) => !isNaN(Date.parse(date)), {
    message: 'Invalid date format',
  }),
})

export const BerthSchema = z.object({
  berth_id: z.number(),
  marina_id: z.number(),
  name: z.string(),
  min_length: z.number(),
  max_length: z.number(),
  boat_types: z.array(z.string()),
  pricing: z.enum(['per_meter', 'flat_fee']),
  price: z.string(),
  available_days: z.array(AvailableDaySchema),
})

export type Berth = z.infer<typeof BerthSchema>

export const EditBerthFormSchema = z
  .object({
    min_length: z
      .union([
        z.string(),
        z.number().min(1, 'Min length must be greater or equal to 1.'),
      ])
      .refine((value) => value !== '' && typeof value === 'number', {
        message: 'Min length is required.',
      }),
    max_length: z
      .union([
        z.string(),
        z.number().min(2, 'Max length must be greater or equal to 2.'),
      ])
      .refine((value) => value !== '' && typeof value === 'number', {
        message: 'Max length is required.',
      }),
    name: z.string(),
    boat_types: z.array(z.string()),
    pricing: z.enum(['per_meter', 'flat_fee']),
    price_per_meter: z.number(),
    price_flat_fee: z.number(),
  })
  .refine((data) => data.max_length > data.min_length, {
    message: 'Max length must be greater or equal to min length.',
    path: ['max_length'],
  })

export type EditBerthFormType = z.infer<typeof EditBerthFormSchema>
